import '../styles/index.scss';
import Head from 'next/head';
import type { AppProps } from 'next/app';
import { configureForceReload } from '../config/configureForceReload';
import { configureAppResumed } from '../config/configureAppResumed';
import ErrorBoundary from '../components/ErrorBoundary';
import ErrorPage from '../components/ErrorPage';
import { Suspense, useCallback, useEffect } from 'react';
import SplashLoader from '../components/SplashLoader';
import SplashLoaderRoot from '../components/SplashLoaderRoot';
import { VERSION_HASH, DEV } from '../config/constants';
import dynamic from 'next/dynamic';
import { logAnalyticsEvent } from '../config/firebase';
import { initSentry } from '../config/sentry';
import { useRouteMemory } from '../util/useRouteMemory';
import { rubik, dmMono } from '../fonts';

let AuthBoundary = dynamic(() => import('../components/AuthBoundary'), {
  ssr: false,
  loading: SplashLoader,
});

initSentry();
configureForceReload();
configureAppResumed();

let App = ({ Component, pageProps }: AppProps) => {
  if (!DEV) console.log(VERSION_HASH);

  let render = useCallback(() => {
    return <Component {...pageProps} />;
  }, [Component, pageProps]);

  let isPublic = pageProps.public;

  useEffect(() => {
    logAnalyticsEvent({
      key: 'pageLoad',
      payload: undefined,
    });
  }, []);

  useRouteMemory();

  return (
    <>
      <style jsx global>{`
        :root {
          --font-rubik: ${rubik.style.fontFamily};
          --font-mono: ${dmMono.style.fontFamily};
        }
      `}</style>
      <Head>
        {/* viewport must be handled here per https://nextjs.org/docs/messages/no-document-viewport-meta */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, viewport-fit=cover"
        />
        <title>Fantasy Oscars</title>
      </Head>
      <div
        id="root"
        className="text-neutral-0 relative overflow-hidden font-sans shadow-2xl"
      >
        <ErrorBoundary fallback={<ErrorPage />}>
          <SplashLoaderRoot>
            <Suspense fallback={<SplashLoader />}>
              {isPublic ? (
                render()
              ) : (
                <AuthBoundary isAdminPage={pageProps.admin}>
                  {render}
                </AuthBoundary>
              )}
            </Suspense>
          </SplashLoaderRoot>
        </ErrorBoundary>
      </div>
    </>
  );
};

export default App;
